import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import { Calendar, IndianRupee, Users, TrendingUp, TrendingDown, Calendar as CalendarIcon, BarChart3, LineChart, PieChart, Activity, ShieldCheck, CreditCard, Fingerprint } from 'lucide-react';
import axiosInstance from '../networking/axiosconfig';

// Relative path — axiosInstance supplies the origin and the auth token.
const LOCAL_API_BASE_URL = '/api';

const extractMonthlySalary = (response) => {
  const payload = response?.data;
  if (!payload) return null;

  const coerce = (value) => {
    if (value === undefined || value === null) return null;
    const num = parseFloat(value);
    return Number.isNaN(num) ? null : num;
  };

  if (typeof payload.monthlySalary === 'number' || typeof payload.monthlySalary === 'string') {
    return coerce(payload.monthlySalary);
  }

  if (typeof payload.monthly_salary === 'number' || typeof payload.monthly_salary === 'string') {
    return coerce(payload.monthly_salary);
  }

  if (payload.salary !== undefined) {
    if (typeof payload.salary === 'number' || typeof payload.salary === 'string') {
      return coerce(payload.salary);
    }
    const salaryObj = payload.salary || {};
    if (salaryObj.monthly_salary !== undefined) return coerce(salaryObj.monthly_salary);
    if (salaryObj.monthlySalary !== undefined) return coerce(salaryObj.monthlySalary);
  }

  if (Array.isArray(payload.data) && payload.data.length > 0) {
    const latestRecord = payload.data[0];
    if (latestRecord.monthly_salary !== undefined) return coerce(latestRecord.monthly_salary);
    if (latestRecord.monthlySalary !== undefined) return coerce(latestRecord.monthlySalary);
  }

  return null;
};

const Dashboard = () => {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const currentDate = new Date();
  const currentCalendarYear = currentDate.getFullYear();
  const currentMonthIndex = currentDate.getMonth();
  const currentMonthName = monthNames[currentMonthIndex];

  const [selectedMonth, setSelectedMonth] = useState(currentMonthName);
  const [selectedYear, setSelectedYear] = useState(currentCalendarYear);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [publicHolidays, setPublicHolidays] = useState({});
  const [totalPayroll, setTotalPayroll] = useState(null);
  const [totalPayrollLoading, setTotalPayrollLoading] = useState(false);
  const [trendSeries, setTrendSeries] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [chartType, setChartType] = useState('line'); // 'line' | 'candle'
  const [hoveredDay, setHoveredDay] = useState(null);

  const financialYearMonths = [
    'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December', 'January', 'February', 'March'
  ];

  const buildYearOptions = () => {
    const startYear = currentCalendarYear - 10;
    const endYear = currentCalendarYear + 50;
    const years = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return years;
  };

  const availableYears = buildYearOptions();

  const getHolidayInfo = (date, month, year) =>
    publicHolidays[year]?.[month]?.[date] || null;

  const getMonthNumber = (monthName) => monthNames.indexOf(monthName) + 1;

  const getDaysInMonth = (month, year) => {
    const monthIndex = monthNames.indexOf(month);
    return new Date(year, monthIndex + 1, 0).getDate();
  };

  const getDayName = (date, month, year) => {
    const monthIndex = monthNames.indexOf(month);
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dateObj = new Date(year, monthIndex, date);
    return dayNames[dateObj.getDay()];
  };

  const isPublicHoliday = (date, month, year) => Boolean(getHolidayInfo(date, month, year));

  const isSunday = (date, month, year) => {
    return getDayName(date, month, year) === 'Sun';
  };

  const getWorkingDaysInMonth = (month, year) => {
    const totalDays = getDaysInMonth(month, year);
    let workingDays = 0;
    for (let day = 1; day <= totalDays; day++) {
      if (isSunday(day, month, year)) continue;
      if (isPublicHoliday(day, month, year)) continue;
      workingDays++;
    }
    return workingDays;
  };

  const getMonthYear = (month) => {
    const monthIndex = financialYearMonths.indexOf(month);
    return monthIndex <= 8 ? selectedYear : selectedYear + 1;
  };

  const calculateAttendanceStats = (attendance) => {
    let present = 0, cl = 0, pl = 0, absent = 0, sunday = 0, holiday = 0;

    attendance.forEach(day => {
      if (day.status === 'present') present++;
      else if (day.status === 'cl') cl++;
      else if (day.status === 'pl') pl++;
      else if (day.status === 'absent') absent++;
      else if (day.status === 'sunday') sunday++;
      else if (day.status === 'holiday') holiday++;
    });

    return { present, cl, pl, absent, sunday, holiday };
  };

  const getQuarterLeaveStatus = (employeeData, month) => {
    const quarterMap = {
      'April': ['April', 'May', 'June'],
      'May': ['April', 'May', 'June'],
      'June': ['April', 'May', 'June'],
      'July': ['July', 'August', 'September'],
      'August': ['July', 'August', 'September'],
      'September': ['July', 'August', 'September'],
      'October': ['October', 'November', 'December'],
      'November': ['October', 'November', 'December'],
      'December': ['October', 'November', 'December'],
      'January': ['January', 'February', 'March'],
      'February': ['January', 'February', 'March'],
      'March': ['January', 'February', 'March'],
    };

    const quarterMonths = quarterMap[month];
    let totalCL = 0, totalPL = 0, totalUnpaid = 0;

    quarterMonths.forEach(m => {
      if (employeeData[m]) {
        totalCL += employeeData[m].cl || 0;
        totalPL += employeeData[m].pl || 0;
        totalUnpaid += employeeData[m].unpaid || 0;
      }
    });

    return { totalCL, totalPL, totalUnpaid, quarterMonths };
  };

  const calculateSalaryFromAttendance = (employee, month) => {
    const monthData = employee.monthlyData?.[month];
    if (!monthData) {
      // If no month data, use baseSalary (should be set from API)
      const netSalary = employee.baseSalary || 0;
      return {
        netSalary,
        deduction: 0,
        present: 0,
        cl: 0,
        pl: 0,
        unpaid: 0,
        sunday: 0,
        holiday: 0,
        absent: 0
      };
    }

    const stats = calculateAttendanceStats(monthData.attendance || []);
    const quarterStatus = getQuarterLeaveStatus(employee.monthlyData || {}, month);

    let unpaidLeaves = 0;
    const totalPaidLeavesInQuarter = quarterStatus.totalCL + quarterStatus.totalPL;
    const quarterlyPaidLeaveLimit = 4;

    if (totalPaidLeavesInQuarter > quarterlyPaidLeaveLimit) {
      unpaidLeaves = totalPaidLeavesInQuarter - quarterlyPaidLeaveLimit;
    }
    unpaidLeaves += stats.absent;

    // Use netSalary from monthData if available, otherwise fallback to baseSalary
    // Ensure we always return a valid number
    const netSalary = monthData.netSalary !== undefined && monthData.netSalary !== null
      ? monthData.netSalary
      : (employee.baseSalary || 0);
    const deduction = monthData.deduction ?? 0;

    return {
      netSalary: typeof netSalary === 'number' ? netSalary : 0,
      deduction: typeof deduction === 'number' ? deduction : 0,
      present: stats.present,
      cl: stats.cl,
      pl: stats.pl,
      unpaid: unpaidLeaves,
      sunday: stats.sunday,
      holiday: stats.holiday,
      absent: stats.absent
    };
  };

  const fetchAttendanceFromApi = async (userId, month, year) => {
    try {
      const response = await axiosInstance.get(`${LOCAL_API_BASE_URL}/get/attendance`, {
        params: { userId, month, year }
      });
      const data = response?.data;
      if (Array.isArray(data?.data)) return data.data;
      if (Array.isArray(data?.attendance)) return data.attendance;
      return [];
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.error(`Failed to fetch attendance for user ${userId}:`, error.message || error);
      }
      return [];
    }
  };

  const fetchSalarySummaryFromApi = async (userId, month, year) => {
    try {
      const response = await axiosInstance.get(`${LOCAL_API_BASE_URL}/get/netsalary`, {
        params: { userId, month, year }
      });
      const rows = response?.data?.data;
      if (Array.isArray(rows) && rows.length > 0) {
        const latest = rows[0];
        const parseNumber = (value) => {
          if (value === undefined || value === null) return null;
          const num = parseFloat(value);
          return Number.isNaN(num) ? null : num;
        };
        return {
          netSalary: parseNumber(latest.net_salary ?? latest.netSalary),
          deduction: parseNumber(latest.salary_cut ?? latest.salaryCut),
          perDaySalary: parseNumber(latest.per_day_salary ?? latest.perDaySalary),
          monthlySalary: parseNumber(latest.monthly_salary ?? latest.monthlySalary),
          raw: latest
        };
      }
      return null;
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.error(`Failed to fetch salary summary for user ${userId}:`, error.message || error);
      }
      return null;
    }
  };

  const fetchTotalPayrollFromApi = async (month, year) => {
    try {
      setTotalPayrollLoading(true);
      const response = await axiosInstance.post(`${LOCAL_API_BASE_URL}/salary/total-payroll`, {
        month,
        year
      });

      const payrollData = response?.data;
      // Handle different possible response structures
      const totalPayrollValue = payrollData?.total_payroll ??
        payrollData?.totalPayroll ??
        payrollData?.data?.total_payroll ??
        payrollData?.data?.totalPayroll ??
        payrollData?.amount ??
        0;

      const parsedValue = parseFloat(totalPayrollValue);
      setTotalPayroll(isNaN(parsedValue) ? 0 : parsedValue);
    } catch (error) {
      console.error('Failed to fetch total payroll:', error);
      // On error, fallback to calculated value
      setTotalPayroll(null);
    } finally {
      setTotalPayrollLoading(false);
    }
  };

  const buildDefaultAttendance = (monthName, year) => {
    const daysInMonth = getDaysInMonth(monthName, year);
    return Array.from({ length: daysInMonth }, (_, index) => {
      const date = index + 1;
      if (isSunday(date, monthName, year)) {
        return { date, status: 'sunday', dayName: 'Sun', isHoliday: false };
      }
      const holidayInfo = getHolidayInfo(date, monthName, year);
      if (holidayInfo) {
        return {
          date,
          status: 'holiday',
          dayName: getDayName(date, monthName, year),
          isHoliday: true,
          holidayName: holidayInfo.name
        };
      }
      return {
        date,
        status: '',
        dayName: getDayName(date, monthName, year),
        isHoliday: false
      };
    });
  };

  const fetchMonthlyTrend = async (monthName, year) => {
    try {
      setTrendLoading(true);
      const monthNum = getMonthNumber(monthName);
      const res = await axiosInstance.get(`${LOCAL_API_BASE_URL}/attendance/monthly-trend`, {
        params: { month: monthNum, year }
      });
      if (res.data?.status && Array.isArray(res.data?.data)) {
        setTrendSeries(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch attendance trend:', err);
    } finally {
      setTrendLoading(false);
    }
  };

  const transformApiAttendance = (records, monthName, year) => {
    return records
      .map((record) => {
        const rawDate = record.date || record.attendance_date;
        if (!rawDate && rawDate !== 0) return null;
        let day = null;
        if (typeof rawDate === 'number') {
          day = rawDate;
        } else if (typeof rawDate === 'string') {
          const trimmed = rawDate.trim();
          if (/^\d+$/.test(trimmed) && trimmed.length <= 2) {
            day = parseInt(trimmed, 10);
          } else {
            const datePart = trimmed.split('T')[0];
            const parts = datePart.split('-');
            if (parts.length === 3) {
              day = parseInt(parts[2], 10);
            } else {
              const parsedDate = new Date(trimmed);
              if (!Number.isNaN(parsedDate.getTime())) {
                day = parsedDate.getDate();
              }
            }
          }
        }
        if (!day || Number.isNaN(day)) return null;

        const rawStatus = String(record.status || '').toLowerCase().trim();
        let status = rawStatus;
        if (rawStatus === '1') status = 'present';
        else if (rawStatus === '0') status = 'absent';

        const holidayInfo = getHolidayInfo(day, monthName, year);
        const recordHolidayName = record.holiday_name || record.holidayName || record.name;
        const finalHolidayName = recordHolidayName || holidayInfo?.name;
        const isHolidayDay = status === 'holiday' || Boolean(holidayInfo);
        return {
          date: day,
          status,
          dayName: getDayName(day, monthName, year),
          isHoliday: isHolidayDay,
          holidayName: isHolidayDay ? (finalHolidayName || 'Holiday') : undefined
        };
      })
      .filter(Boolean);
  };

  const attachAttendanceToEmployees = async (employeeList, monthName, year) => {
    const monthNumber = getMonthNumber(monthName);

    const updatedEmployees = await Promise.all(
      employeeList.map(async (emp) => {
        const apiRecords = await fetchAttendanceFromApi(emp.id, monthNumber, year);
        const attendanceEntries = apiRecords.length
          ? transformApiAttendance(apiRecords, monthName, year)
          : buildDefaultAttendance(monthName, year);

        const stats = calculateAttendanceStats(attendanceEntries);
        const salarySummary = await fetchSalarySummaryFromApi(emp.id, monthNumber, year);
        const previousMonthData = emp.monthlyData?.[monthName] || {};

        return {
          ...emp,
          monthlyData: {
            ...emp.monthlyData,
            [monthName]: {
              ...(emp.monthlyData?.[monthName] || {}),
              attendance: attendanceEntries,
              records: attendanceEntries,
              present: stats.present,
              cl: stats.cl,
              pl: stats.pl,
              absent: stats.absent,
              sunday: stats.sunday,
              holiday: stats.holiday,
              netSalary: salarySummary?.netSalary ?? previousMonthData.netSalary ?? emp.baseSalary,
              deduction: salarySummary?.deduction ?? previousMonthData.deduction ?? 0,
              serverSalary: salarySummary
            }
          }
        };
      })
    );

    return updatedEmployees;
  };

  const buildHolidayMap = (records = []) => {
    const map = {};
    records.forEach((holiday) => {
      const dateObj = new Date(holiday.date);
      if (Number.isNaN(dateObj.getTime())) return;
      const year = dateObj.getFullYear();
      const monthName = monthNames[dateObj.getMonth()];
      const day = dateObj.getDate();

      if (!map[year]) map[year] = {};
      if (!map[year][monthName]) map[year][monthName] = {};
      map[year][monthName][day] = {
        day,
        name: holiday.holiday_name || holiday.holidayName || holiday.name || 'Holiday',
        raw: holiday
      };
    });
    return map;
  };

  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const response = await axiosInstance.get(`${LOCAL_API_BASE_URL}/get/holiday`);
        const records = response.data?.data || [];
        setPublicHolidays(buildHolidayMap(records));
      } catch (err) {
        console.error('Failed to fetch holidays:', err);
      }
    };

    fetchHolidays();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      const token = localStorage.getItem("token");

      if (!token || !token.trim()) {
        setError('Please login to access this page.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await axiosInstance.get('/api/users', {
          headers: {
            Authorization: `Bearer ${token.trim()}`
          }
        });

        const employeesData = response.data?.data || [];

        // Filter out inactive employees (is_active = 1 means inactive)
        const activeEmployeesData = employeesData.filter(emp => emp.is_active !== 1);

        const transformedEmployees = await Promise.all(
          activeEmployeesData.map(async (emp) => {
            let avatar = '👤';
            if (emp.gender === 'Male') avatar = '👨';
            else if (emp.gender === 'Female') avatar = '👩';

            // Try to get baseSalary from employee data first (check multiple fields)
            let baseSalary = emp.base_salary || emp.monthly_salary || 0;
            try {
              const salaryResponse = await axiosInstance.get(`${LOCAL_API_BASE_URL}/salary/get`, {
                params: { userId: emp.user_id_PK }
              });
              const savedSalary = extractMonthlySalary(salaryResponse);
              if (savedSalary) {
                const parsedSalary = parseFloat(savedSalary);
                if (!isNaN(parsedSalary) && parsedSalary > 0) {
                  baseSalary = parsedSalary;
                }
              }
            } catch (salaryErr) {
              if (salaryErr?.response?.status !== 404) {
                console.error(`Error fetching salary for user ${emp.user_id_PK}:`, salaryErr.message);
              }
            }

            // Debug: Log if baseSalary is 0 or missing
            if (!baseSalary || baseSalary === 0) {
              console.warn(`Employee ${emp.user_id_PK} (${emp.first_name} ${emp.last_name}) has no baseSalary set`);
            }

            return {
              id: emp.user_id_PK,
              name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Unknown',
              avatar: emp.user_profile_img || avatar,
              baseSalary,
              monthlyData: {},
              email: emp.email,
              designation: emp.designation_name,
              department: emp.department_name,
              isActive: emp.is_active === 0
            };
          })
        );

        const yearForSelectedMonth = getMonthYear(selectedMonth);
        const employeesWithAttendance = await attachAttendanceToEmployees(
          transformedEmployees,
          selectedMonth,
          yearForSelectedMonth
        );

        setEmployees(employeesWithAttendance);
      } catch (err) {
        console.error('Error fetching employees:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Failed to load employees.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    if (!employees.length) return;
    const year = getMonthYear(selectedMonth);

    const refreshAttendance = async () => {
      const updated = await attachAttendanceToEmployees(employees, selectedMonth, year);
      setEmployees(updated);
    };

    refreshAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, publicHolidays]);

  useEffect(() => {
    const monthNumber = getMonthNumber(selectedMonth);
    const year = getMonthYear(selectedMonth);
    fetchTotalPayrollFromApi(monthNumber, year);
    fetchMonthlyTrend(selectedMonth, year);
  }, [selectedMonth, selectedYear]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <p className="text-red-600 font-semibold mb-4">{error}</p>
        </div>
      </div>
    );
  }

  const daysInSelectedMonth = getDaysInMonth(selectedMonth, getMonthYear(selectedMonth));

  // Day-by-day attendance trends for interactive graphical representation
  const dailyAttendanceTrends = Array.from({ length: daysInSelectedMonth }, (_, idx) => {
    const day = idx + 1;
    const year = getMonthYear(selectedMonth);
    const dayName = getDayName(day, selectedMonth, year);
    const sunday = isSunday(day, selectedMonth, year);
    const holiday = isPublicHoliday(day, selectedMonth, year);

    let presentCount = 0;
    let absentCount = 0;

    employees.forEach((emp) => {
      const records = emp.monthlyData?.[selectedMonth]?.attendance || emp.monthlyData?.[selectedMonth]?.records || [];
      const dayRecord = records.find((r) => Number(r.date) === Number(day));
      if (dayRecord) {
        const st = String(dayRecord.status || '').toLowerCase().trim();
        if (st === 'present' || st === 'half-day' || st === '1' || st === 'extrapay') {
          presentCount++;
        } else if (st === 'absent' || st === '0') {
          absentCount++;
        }
      }
    });

    return {
      day,
      dayName,
      isSunday: sunday,
      isHoliday: holiday,
      present: presentCount,
      absent: absentCount
    };
  });

  const totalPresentSum = employees.reduce((sum, emp) => sum + calculateSalaryFromAttendance(emp, selectedMonth).present, 0);
  const totalAbsentSum = employees.reduce((sum, emp) => sum + calculateSalaryFromAttendance(emp, selectedMonth).absent, 0);
  const totalLeaveSum = employees.reduce((sum, emp) => {
    const s = calculateSalaryFromAttendance(emp, selectedMonth);
    return sum + (s.cl || 0) + (s.pl || 0) + (s.unpaid || 0);
  }, 0);
  const totalPossible = Math.max(1, totalPresentSum + totalAbsentSum + totalLeaveSum);
  const presentPercent = Math.round((totalPresentSum / totalPossible) * 100);
  const absentPercent = Math.round((totalAbsentSum / totalPossible) * 100);
  const leavePercent = Math.max(0, 100 - presentPercent - absentPercent);

  const departmentCounts = {};
  employees.forEach((emp) => {
    const dept = emp.department || 'General';
    departmentCounts[dept] = (departmentCounts[dept] || 0) + 1;
  });

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-16">
      <Navbar />
      <div className="max-w-7xl mx-auto p-6">

        <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 rounded-2xl shadow-2xl p-8 mb-6 text-white">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-3 mb-2">
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <IndianRupee className="text-white" size={36} />
                </div>
                Employee Salary Management System
              </h1>
              <p className="text-blue-100 mt-2 text-lg">Financial Year: April 2024 - March 2025</p>
            </div>
            <div className="bg-white bg-opacity-20 backdrop-blur-sm px-6 py-4 rounded-xl border border-white border-opacity-30">
              <div className="flex items-center gap-2 mb-2">
                <CalendarIcon size={18} />
                <p className="text-sm font-semibold">
                  Working Days: {getWorkingDaysInMonth(selectedMonth, getMonthYear(selectedMonth))} days
                </p>
              </div>
              <p className="text-xs text-blue-100">{selectedMonth} {getMonthYear(selectedMonth)}</p>
              <div className="mt-2 pt-2 border-t border-white border-opacity-20">
                <p className="text-xs text-blue-100">Quarterly Limit: 4 Paid Leaves (CL+PL)</p>
              </div>
            </div>
          </div>
        </div>
        {/* Month & Year Selector */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-100">
          <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
            <Calendar className="text-blue-600" size={20} />
            <span>Select Month & Year</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-2">Month</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white hover:border-gray-300 cursor-pointer"
              >
                {financialYearMonths.map((month) => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-2">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white hover:border-gray-300 cursor-pointer"
              >
                {availableYears.map((yearOption) => (
                  <option key={yearOption} value={yearOption}>
                    {yearOption}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Summary Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-800">Summary for {selectedMonth} {getMonthYear(selectedMonth)}</h3>
              <p className="text-sm text-gray-500 mt-1">Complete overview of salary and attendance</p>
            </div>
            <div className="hidden md:flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg">
              <CalendarIcon className="text-blue-600" size={20} />
              <span className="text-sm font-semibold text-blue-700">{selectedMonth} {getMonthYear(selectedMonth)}</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Total Employees Card */}
            <div className="group relative bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 text-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <Users size={24} />
                  </div>
                  <TrendingUp className="opacity-80" size={20} />
                </div>
                <p className="text-sm opacity-90 mb-2 font-medium">Total Employees</p>
                <p className="text-4xl font-bold mb-1">{employees.length}</p>
                <p className="text-xs opacity-75">Active workforce</p>
              </div>
            </div>

            {/* Total Payroll Card */}
            <div className="group relative bg-gradient-to-br from-green-500 via-green-600 to-emerald-700 text-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <IndianRupee size={24} />
                  </div>
                  <TrendingUp className="opacity-80" size={20} />
                </div>
                <p className="text-sm opacity-90 mb-2 font-medium">Total Payroll</p>
                <p className="text-4xl font-bold mb-1">
                  {totalPayrollLoading ? (
                    <span className="text-2xl">Loading...</span>
                  ) : (
                    `₹${(totalPayroll !== null ? totalPayroll : employees.reduce((sum, emp) => {
                      const salaryData = calculateSalaryFromAttendance(emp, selectedMonth);
                      // Use netSalary if available, otherwise use baseSalary, ensure it's a number
                      const salary = salaryData.netSalary || emp.baseSalary || 0;
                      return sum + (typeof salary === 'number' ? salary : 0);
                    }, 0)).toLocaleString()}`
                  )}
                </p>
                <p className="text-xs opacity-75">Net salary distribution</p>
              </div>
            </div>

            {/* Total Deductions Card */}
            <div className="group relative bg-gradient-to-br from-red-500 via-red-600 to-rose-700 text-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <TrendingDown size={24} />
                  </div>
                  <TrendingDown className="opacity-80" size={20} />
                </div>
                <p className="text-sm opacity-90 mb-2 font-medium">Total Deductions</p>
                <p className="text-4xl font-bold mb-1">
                  ₹{employees.reduce((sum, emp) => sum + calculateSalaryFromAttendance(emp, selectedMonth).deduction, 0).toLocaleString()}
                </p>
                <p className="text-xs opacity-75">Salary cuts</p>
              </div>
            </div>

            {/* Avg Present Days Card */}
            <div className="group relative bg-gradient-to-br from-purple-500 via-purple-600 to-indigo-700 text-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <CalendarIcon size={24} />
                  </div>
                  <TrendingUp className="opacity-80" size={20} />
                </div>
                <p className="text-sm opacity-90 mb-2 font-medium">Avg Present Days</p>
                <p className="text-4xl font-bold mb-1">
                  {employees.length > 0 ? Math.round(employees.reduce((sum, emp) => sum + calculateSalaryFromAttendance(emp, selectedMonth).present, 0) / employees.length) : 0}
                </p>
                <p className="text-xs opacity-75">Average attendance days</p>
              </div>
            </div>
          </div>
        </div>

        {/* 📊 GRAPHICAL REPRESENTATION & ANALYTICS SUITE */}
        <div className="mt-8 space-y-6">
          {/* Chart 1: Daily Attendance Activity Trend Line & Candle Chart */}
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-100">
            {(() => {
              const activeTrends = (trendSeries && trendSeries.length > 0)
                ? trendSeries
                : dailyAttendanceTrends;
              const totalRecordedPresents = activeTrends.reduce((sum, t) => sum + (t.present || 0), 0);
              const totalRecordedPunches = activeTrends.reduce((sum, t) => sum + (t.punches || 0), 0);
              const maxDayAttendance = Math.max(...activeTrends.map((t) => t.present || 0), 0);
              const maxScale = Math.max(maxDayAttendance, 5);
              const activeRecordedDays = activeTrends.filter((t) => (t.present || 0) > 0).length;

              // SVG geometry
              const padL = 40;
              const padR = 40;
              const padT = 36;
              const padB = 40;
              const svgW = 1000;
              const svgH = 240;
              const chartW = svgW - padL - padR;
              const chartH = svgH - padT - padB;
              const baselineY = svgH - padB;
              const count = activeTrends.length || 1;
              const step = chartW / Math.max(count - 1, 1);

              const coords = activeTrends.map((t, i) => {
                const x = padL + (count === 1 ? chartW / 2 : i * step);
                const val = t.present || 0;
                const y = baselineY - (val / maxScale) * chartH;
                return { ...t, x, y, val };
              });

              const polylinePoints = coords.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ');
              const polygonPoints = `${coords[0]?.x.toFixed(1) || padL},${baselineY} ${polylinePoints} ${coords[coords.length - 1]?.x.toFixed(1) || (svgW - padR)},${baselineY}`;

              return (
                <>
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600">
                          {chartType === 'line' ? <LineChart size={22} /> : <BarChart3 size={22} />}
                        </div>
                        <h3 className="text-xl font-bold text-gray-800">
                          Daily Attendance Activity Trend — {selectedMonth} {getMonthYear(selectedMonth)}
                        </h3>
                      </div>
                      <p className="text-xs text-gray-500">
                        Real-time daily presence curve based strictly on database attendance records and punches.
                      </p>
                    </div>

                    {/* Chart Mode Toggle & Stats */}
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex items-center bg-gray-100 p-1 rounded-xl border border-gray-200">
                        <button
                          type="button"
                          onClick={() => setChartType('line')}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            chartType === 'line'
                              ? 'bg-white text-emerald-700 shadow-xs'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <LineChart size={14} />
                          Line Chart
                        </button>
                        <button
                          type="button"
                          onClick={() => setChartType('candle')}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            chartType === 'candle'
                              ? 'bg-white text-emerald-700 shadow-xs'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <BarChart3 size={14} />
                          Candle / Bar
                        </button>
                      </div>

                      <div className="flex items-center gap-2 text-xs font-semibold">
                        <span className="flex items-center gap-1.5 text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200">
                          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                          Recorded: {activeRecordedDays} Day{activeRecordedDays === 1 ? '' : 's'}
                        </span>
                        <span className="flex items-center gap-1.5 text-blue-700 bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-200">
                          Total Attendance: {totalRecordedPresents}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Hover Information Banner */}
                  {hoveredDay && (
                    <div className="mb-3 px-4 py-2.5 bg-gradient-to-r from-slate-900 to-indigo-950 text-white rounded-xl shadow-md flex flex-wrap items-center justify-between text-xs animate-fadeIn">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-emerald-400">Day {hoveredDay.day} ({hoveredDay.dayName})</span>
                        <span className="text-gray-400">|</span>
                        <span>{hoveredDay.date || `${selectedMonth} ${hoveredDay.day}`}</span>
                        {hoveredDay.isSunday && <span className="text-red-400 font-semibold">(Sunday)</span>}
                      </div>
                      <div className="flex items-center gap-4 font-semibold">
                        <span className="text-emerald-300">Staff Present: {hoveredDay.present || 0}</span>
                        {hoveredDay.punches > 0 && (
                          <span className="text-cyan-300">Machine Punches: {hoveredDay.punches}</span>
                        )}
                        <span className="text-gray-300">
                          {hoveredDay.present > 0 ? '🟢 Attendance Logged' : '⚪ No Data / 0 Present'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* CHART VIEW */}
                  {chartType === 'line' ? (
                    /* 📈 INTERACTIVE SVG LINE CHART */
                    <div className="overflow-x-auto pb-2">
                      <div className="min-w-[760px] bg-gradient-to-b from-slate-50/60 to-white rounded-xl p-3 border border-gray-100">
                        <svg
                          viewBox={`0 0 ${svgW} ${svgH}`}
                          className="w-full h-64 select-none"
                          style={{ overflow: 'visible' }}
                        >
                          <defs>
                            <linearGradient id="attendanceAreaGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
                              <stop offset="60%" stopColor="#10b981" stopOpacity="0.1" />
                              <stop offset="100%" stopColor="#10b981" stopOpacity="0.0" />
                            </linearGradient>
                            <linearGradient id="attendanceLineGrad" x1="0" y1="0" x2="1" y2="0">
                              <stop offset="0%" stopColor="#059669" />
                              <stop offset="50%" stopColor="#10b981" />
                              <stop offset="100%" stopColor="#0d9488" />
                            </linearGradient>
                          </defs>

                          {/* Horizontal Gridlines */}
                          {[0, 0.5, 1].map((pct, idx) => {
                            const y = baselineY - pct * chartH;
                            const labelVal = Math.round(pct * maxScale);
                            return (
                              <g key={idx}>
                                <line
                                  x1={padL}
                                  y1={y}
                                  x2={svgW - padR}
                                  y2={y}
                                  stroke="#e2e8f0"
                                  strokeDasharray="4 4"
                                  strokeWidth="1"
                                />
                                <text
                                  x={padL - 10}
                                  y={y + 3}
                                  textAnchor="end"
                                  fontSize="10"
                                  fontWeight="600"
                                  fill="#94a3b8"
                                >
                                  {labelVal}
                                </text>
                              </g>
                            );
                          })}

                          {/* Area Polygon Fill */}
                          <polygon points={polygonPoints} fill="url(#attendanceAreaGrad)" />

                          {/* Line Polyline Stroke */}
                          <polyline
                            points={polylinePoints}
                            fill="none"
                            stroke="url(#attendanceLineGrad)"
                            strokeWidth="3.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />

                          {/* Active Hover Guideline */}
                          {hoveredDay && (
                            <line
                              x1={coords.find((c) => c.day === hoveredDay.day)?.x || padL}
                              y1={padT}
                              x2={coords.find((c) => c.day === hoveredDay.day)?.x || padL}
                              y2={baselineY}
                              stroke="#3b82f6"
                              strokeWidth="1.5"
                              strokeDasharray="3 3"
                            />
                          )}

                          {/* Data Nodes & Pill Count Badges */}
                          {coords.map((c) => {
                            const hasAttendance = c.val > 0;
                            const isHovered = hoveredDay?.day === c.day;

                            return (
                              <g key={c.day}>
                                {/* Node on line */}
                                {hasAttendance ? (
                                  <>
                                    <circle
                                      cx={c.x}
                                      cy={c.y}
                                      r={isHovered ? "11" : "8"}
                                      fill="#10b981"
                                      opacity={isHovered ? "0.4" : "0.25"}
                                    />
                                    <circle
                                      cx={c.x}
                                      cy={c.y}
                                      r={isHovered ? "6" : "5"}
                                      fill="#059669"
                                      stroke="#ffffff"
                                      strokeWidth="2.5"
                                    />

                                    {/* Count Pill directly on top of the node */}
                                    <g transform={`translate(${c.x}, ${c.y - 24})`}>
                                      <rect
                                        x="-14"
                                        y="-10"
                                        width="28"
                                        height="18"
                                        rx="9"
                                        fill="#047857"
                                        stroke="#ffffff"
                                        strokeWidth="1.5"
                                        filter="drop-shadow(0 2px 3px rgba(0,0,0,0.25))"
                                      />
                                      <text
                                        x="0"
                                        y="3"
                                        textAnchor="middle"
                                        fill="#ffffff"
                                        fontSize="10"
                                        fontWeight="800"
                                      >
                                        {c.val}
                                      </text>
                                    </g>
                                  </>
                                ) : (
                                  /* Zero count indicator at baseline */
                                  <circle
                                    cx={c.x}
                                    cy={baselineY}
                                    r={c.isSunday ? "2.5" : "2"}
                                    fill={c.isSunday ? "#f87171" : "#cbd5e1"}
                                  />
                                )}

                                {/* X-Axis Day Number */}
                                <text
                                  x={c.x}
                                  y={baselineY + 18}
                                  textAnchor="middle"
                                  fontSize="10"
                                  fontWeight={isHovered || hasAttendance ? "800" : "600"}
                                  fill={c.isSunday ? "#ef4444" : hasAttendance ? "#047857" : "#64748b"}
                                >
                                  {c.day}
                                </text>

                                {/* Transparent hover hit area */}
                                <rect
                                  x={c.x - step / 2}
                                  y="0"
                                  width={step}
                                  height={svgH}
                                  fill="transparent"
                                  cursor="pointer"
                                  onMouseEnter={() => setHoveredDay(c)}
                                  onMouseLeave={() => setHoveredDay(null)}
                                />
                              </g>
                            );
                          })}
                        </svg>
                      </div>
                    </div>
                  ) : (
                    /* 📊 DATABASE-DRIVEN CANDLE / BAR CHART */
                    <div className="overflow-x-auto pb-2">
                      <div className="min-w-[760px] h-60 flex items-end gap-1.5 sm:gap-2 border-b border-gray-200 px-3 pt-8">
                        {coords.map((t) => {
                          const hasData = t.val > 0;
                          const barHeight = hasData
                            ? Math.max(34, Math.round((t.val / maxScale) * 150))
                            : 4; // Flat baseline when 0 data, NO fake candles

                          return (
                            <div
                              key={t.day}
                              onMouseEnter={() => setHoveredDay(t)}
                              onMouseLeave={() => setHoveredDay(null)}
                              className="flex-1 flex flex-col items-center justify-end h-full group relative cursor-pointer"
                            >
                              {/* Count Pill Badge above candle */}
                              {hasData && (
                                <div className="mb-1.5 px-1.5 py-0.5 rounded-full bg-emerald-700 text-white text-[10px] font-extrabold shadow-sm">
                                  {t.val}
                                </div>
                              )}

                              {/* Candle Bar (ONLY rises when data exists in database) */}
                              <div className="w-full flex-1 flex items-end justify-center pb-2">
                                <div
                                  className={`w-full max-w-[22px] rounded-t-md transition-all duration-300 group-hover:scale-110 shadow-xs ${
                                    hasData
                                      ? 'bg-gradient-to-t from-emerald-600 to-emerald-400 ring-2 ring-emerald-300'
                                      : t.isSunday
                                      ? 'bg-slate-200'
                                      : 'bg-gray-200'
                                  }`}
                                  style={{ height: `${barHeight}px` }}
                                />
                              </div>

                              {/* Day Label */}
                              <span
                                className={`text-[10px] font-bold ${
                                  t.isSunday ? 'text-red-500' : hasData ? 'text-emerald-700' : 'text-gray-400'
                                }`}
                              >
                                {t.day}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Footer note */}
                  <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between text-xs text-gray-500 gap-2 pt-2 border-t border-gray-100">
                    <p>
                      {activeRecordedDays > 0 ? (
                        <>
                          <span className="font-semibold text-emerald-700">✓ Real DB Data Active:</span> Found {totalRecordedPresents} attendance records across {activeRecordedDays} day(s) for {selectedMonth} {getMonthYear(selectedMonth)}.
                        </>
                      ) : (
                        <>
                          <span className="font-semibold text-gray-600">ℹ No Punches Logged:</span> No employee attendance found in database for this month yet. Punches will appear automatically when punched on biometric machine.
                        </>
                      )}
                    </p>
                    <p className="text-[11px] text-gray-400">
                      T700 Biometric Sync: Active Cloud Port 4002
                    </p>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Two Columns: Visual Breakdown & Biometric Share */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Chart 2: Attendance Ratio & Distribution Breakdown */}
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-100">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600">
                  <PieChart size={22} />
                </div>
                <h3 className="text-lg font-bold text-gray-800">
                  Monthly Attendance Distribution Ratio
                </h3>
              </div>
              <p className="text-xs text-gray-500 mb-6">
                Proportion of workdays attended vs leaves and absences for the active team.
              </p>

              <div className="space-y-4">
                {/* Present Bar */}
                <div>
                  <div className="flex justify-between text-xs font-semibold mb-1.5">
                    <span className="flex items-center gap-1.5 text-gray-700">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                      Present Rate
                    </span>
                    <span className="text-emerald-700 font-bold">{presentPercent}% ({totalPresentSum} Days)</span>
                  </div>
                  <div className="w-full bg-gray-100 h-3 rounded-full overflow-hidden">
                    <div
                      className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                      style={{ width: `${presentPercent}%` }}
                    />
                  </div>
                </div>

                {/* Absent Bar */}
                <div>
                  <div className="flex justify-between text-xs font-semibold mb-1.5">
                    <span className="flex items-center gap-1.5 text-gray-700">
                      <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                      Absent Rate
                    </span>
                    <span className="text-rose-700 font-bold">{absentPercent}% ({totalAbsentSum} Days)</span>
                  </div>
                  <div className="w-full bg-gray-100 h-3 rounded-full overflow-hidden">
                    <div
                      className="bg-rose-500 h-full rounded-full transition-all duration-500"
                      style={{ width: `${absentPercent}%` }}
                    />
                  </div>
                </div>

                {/* Leaves Bar */}
                <div>
                  <div className="flex justify-between text-xs font-semibold mb-1.5">
                    <span className="flex items-center gap-1.5 text-gray-700">
                      <span className="w-2.5 h-2.5 rounded-full bg-indigo-500" />
                      Paid / Unpaid Leaves
                    </span>
                    <span className="text-indigo-700 font-bold">{leavePercent}% ({totalLeaveSum} Days)</span>
                  </div>
                  <div className="w-full bg-gray-100 h-3 rounded-full overflow-hidden">
                    <div
                      className="bg-indigo-500 h-full rounded-full transition-all duration-500"
                      style={{ width: `${leavePercent}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Chart 3: Biometric Hardware & Department Allocation */}
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-100">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-xl bg-purple-50 text-purple-600">
                  <Activity size={22} />
                </div>
                <h3 className="text-lg font-bold text-gray-800">
                  Biometric Hardware & Department Allocation
                </h3>
              </div>
              <p className="text-xs text-gray-500 mb-6">
                Active machine integrations and workforce team distribution.
              </p>

              {/* Hardware Status Banner */}
              <div className="p-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 mb-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold shadow-md shadow-blue-500/20">
                    <ShieldCheck size={20} />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900">T700 / T800 Biometric Fleet</h4>
                    <p className="text-xs text-blue-700 font-medium">Real-Time Cloud Push Sync Active</p>
                  </div>
                </div>
                <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                  ● Connected
                </span>
              </div>

              {/* Department Distribution */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">
                  Department Allocation
                </h4>
                <div className="space-y-2">
                  {Object.entries(departmentCounts).map(([dept, count]) => {
                    const pct = Math.round((count / Math.max(1, employees.length)) * 100);
                    return (
                      <div key={dept} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-xl text-xs">
                        <span className="font-semibold text-gray-800">{dept}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">{count} Staff</span>
                          <span className="px-2 py-0.5 rounded font-bold bg-blue-100 text-blue-700 text-[11px]">
                            {pct}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
